// frontend/src/pages/MyTrashPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// My Trash — Per-user deleted items bin.
//
// Lets any authenticated user browse, restore, or permanently purge their own
// soft-deleted Flows, Dashboards and Data Models.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api/index.js';
import { useNotification } from '../context/NotificationContext.jsx';
import {
  PageHeader,
  LoadingSpinner,
  ConfirmModal,
} from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';

// ─── API helpers ─────────────────────────────────────────────────────────────
const MY_TRASH_BASE = '/my/trash';

const fetchMyItems = (token, table = null, page = 1, pageSize = 50) => {
  const params = new URLSearchParams({ page, page_size: pageSize });
  if (table) params.append('table', table);
  return apiFetch(`${MY_TRASH_BASE}/items?${params.toString()}`, token);
};

const restoreMyRecord = (token, table, recordId) =>
  apiFetch(`${MY_TRASH_BASE}/restore`, token, {
    method: 'POST',
    body: JSON.stringify({ table, record_id: recordId }),
  });

const purgeMyRecord = (token, table, recordId) =>
  apiFetch(`${MY_TRASH_BASE}/purge`, token, {
    method: 'POST',
    body: JSON.stringify({ table, record_id: recordId }),
  });

// ─── Type config ─────────────────────────────────────────────────────────────
const TABS = [
  { key: null,          label: 'All',         icon: 'Layers' },
  { key: 'flows',       label: 'Flows',       icon: 'Workflow' },
  { key: 'dashboards',  label: 'Dashboards',  icon: 'LayoutDashboard' },
  { key: 'datamodels',  label: 'Data Models',  icon: 'Database' },
];

const STAT_CARDS = [
  { typeLabel: 'Flow',       label: 'Flows',       icon: 'Workflow',        accent: 'violet', gradient: 'from-violet-500 to-purple-400' },
  { typeLabel: 'Dashboard',  label: 'Dashboards',  icon: 'LayoutDashboard', accent: 'blue',   gradient: 'from-blue-500 to-cyan-400' },
  { typeLabel: 'Data Model', label: 'Data Models',  icon: 'Database',        accent: 'emerald', gradient: 'from-emerald-500 to-teal-400' },
];

const TYPE_BADGE = {
  Flow:         { bg: 'bg-violet-50 dark:bg-violet-900/30',  text: 'text-violet-700 dark:text-violet-300',  border: 'border-violet-200 dark:border-violet-800' },
  Dashboard:    { bg: 'bg-blue-50 dark:bg-blue-900/30',      text: 'text-blue-700 dark:text-blue-300',      border: 'border-blue-200 dark:border-blue-800' },
  'Data Model': { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800' },
};

const TYPE_ICON = {
  Flow:         'Workflow',
  Dashboard:    'LayoutDashboard',
  'Data Model': 'Database',
};


// ─── SummaryCard (matches AdminTrashPage design) ─────────────────────────────
const SummaryCard = ({ icon, label, value, gradient, accent }) => (
  <div className="relative overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700
      bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 p-5 group flex-1 min-w-[160px]">
    {/* Gradient accent bar */}
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${gradient}`} />
    <div className="flex items-start gap-3">
      <div className={`p-2.5 rounded-xl transition-transform group-hover:scale-110
        ${accent === 'violet'  ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400' :
          accent === 'blue'    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
          accent === 'emerald' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
          'bg-gray-50 dark:bg-gray-700 text-gray-600'
        }`}>
        <DynamicIcon name={icon} className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-extrabold text-gray-900 dark:text-white tabular-nums">{value}</p>
      </div>
    </div>
  </div>
);


// ─── EmptyState ──────────────────────────────────────────────────────────────
const EmptyState = ({ filtered }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="relative mb-5">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-50
                      dark:from-gray-700 dark:to-gray-800 flex items-center justify-center shadow-inner">
        <DynamicIcon name="Trash2" className="w-9 h-9 text-gray-300 dark:text-gray-600" />
      </div>
      <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-green-50 dark:bg-green-900/30
                      flex items-center justify-center border-2 border-white dark:border-gray-800 shadow-sm">
        <DynamicIcon name="CheckCircle" className="w-4 h-4 text-green-500" />
      </div>
    </div>
    <h3 className="text-base font-bold text-gray-600 dark:text-gray-300 mb-1">
      {filtered ? 'No items of this type' : 'Your trash is empty'}
    </h3>
    <p className="text-sm text-gray-400 max-w-xs leading-relaxed">
      {filtered
        ? 'Try switching to "All" to see everything, or check a different category.'
        : 'Items you delete (Flows, Dashboards, Data Models) will appear here so you can restore them.'}
    </p>
  </div>
);


// ─── ItemRow (matches AdminTrashPage ItemRow design) ─────────────────────────
const ItemRow = ({ item, onRestore, onPurge }) => {
  const deletedDate = item.deleted_at
    ? new Date(item.deleted_at).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';

  const iconName   = TYPE_ICON[item.type]  || 'File';
  const badge      = TYPE_BADGE[item.type] || { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };

  return (
    <div className="group flex items-center gap-4 px-5 py-4
                    border-b border-gray-100 dark:border-gray-700/50 last:border-0
                    hover:bg-gray-50/70 dark:hover:bg-gray-700/20 transition-colors duration-150">

      {/* Type icon */}
      <div className={`shrink-0 w-10 h-10 rounded-xl ${badge.bg}
                      flex items-center justify-center
                      group-hover:scale-105 transition-transform duration-200`}>
        <DynamicIcon name={iconName} className={`w-5 h-5 ${badge.text}`} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
            {item.name}
          </p>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border ${badge.bg} ${badge.text} ${badge.border}`}>
            {item.type}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <DynamicIcon name="Calendar" className="w-3 h-3 shrink-0" />
            {deletedDate}
          </span>
        </div>
      </div>

      {/* Actions — visible on hover */}
      <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <button
          onClick={() => onRestore(item)}
          title="Restore"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                     bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400
                     border border-green-200 dark:border-green-800
                     hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
        >
          <DynamicIcon name="RotateCcw" className="w-3.5 h-3.5" />
          Restore
        </button>
        <button
          onClick={() => onPurge(item)}
          title="Delete forever"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                     bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400
                     border border-red-200 dark:border-red-800
                     hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
        >
          <DynamicIcon name="Trash2" className="w-3.5 h-3.5" />
          Delete Forever
        </button>
      </div>
    </div>
  );
};


// ─── MyTrashPage ─────────────────────────────────────────────────────────────
const MyTrashPage = ({ token }) => {
  const { addNotification } = useNotification();

  // ---------- State ----------
  const [loading, setLoading]           = useState(true);
  const [items, setItems]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [countsByType, setCountsByType] = useState({});
  const [activeTab, setActiveTab]       = useState(null);   // null = All
  const [page, setPage]                 = useState(1);
  const PAGE_SIZE = 50;

  // Confirm modals
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [purgeTarget, setPurgeTarget]     = useState(null);
  const [acting, setActing]               = useState(false);

  // ---------- Fetch ----------
  const load = useCallback(async (tableFilter = null, pg = 1) => {
    setLoading(true);
    try {
      const data = await fetchMyItems(token, tableFilter, pg, PAGE_SIZE);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setCountsByType(data.counts_by_type || {});
    } catch (err) {
      addNotification(err?.message || 'Failed to load trash', 'error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load(activeTab, page);
  }, [activeTab, page, load]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPage(1);
  };

  // ---------- Restore ----------
  const handleRestore = async () => {
    if (!restoreTarget) return;
    setActing(true);
    try {
      await restoreMyRecord(token, restoreTarget.table, restoreTarget.id);
      addNotification(`"${restoreTarget.name}" has been restored successfully.`, 'success');
      load(activeTab, page);
    } catch (err) {
      addNotification(err?.message || 'Restore failed', 'error');
    } finally {
      setActing(false);
      setRestoreTarget(null);
    }
  };

  // ---------- Purge ----------
  const handlePurge = async () => {
    if (!purgeTarget) return;
    setActing(true);
    try {
      await purgeMyRecord(token, purgeTarget.table, purgeTarget.id);
      addNotification(`"${purgeTarget.name}" has been permanently deleted.`, 'success');
      load(activeTab, page);
    } catch (err) {
      addNotification(err?.message || 'Delete failed', 'error');
    } finally {
      setActing(false);
      setPurgeTarget(null);
    }
  };

  // ---------- Derived ----------
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const totalAll   = Object.values(countsByType).reduce((s, v) => s + v, 0);

  const tabCount = (typeLabel) => {
    if (!typeLabel) return totalAll;
    return countsByType[typeLabel] || 0;
  };

  // ---------- Render ----------
  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
        <span className="ml-3 text-gray-500">Loading your trash…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <PageHeader
        title="My Trash"
        description="Browse, restore, or permanently delete your soft-deleted Flows, Dashboards and Data Models."
      >
        <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
          <DynamicIcon name="Info" className="w-4 h-4 shrink-0" />
          Only items <span className="font-semibold text-gray-600 dark:text-gray-300 mx-1">you</span> deleted appear here.
        </div>
      </PageHeader>

      {/* ── Summary Cards (3 across, matching Admin style) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {STAT_CARDS.map(({ typeLabel, label, icon, accent, gradient }) => (
          <SummaryCard
            key={typeLabel}
            icon={icon}
            label={label}
            value={countsByType[typeLabel]?.toLocaleString() || '0'}
            accent={accent}
            gradient={gradient}
          />
        ))}
      </div>

      {/* ── Main card ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg
                      border border-gray-100 dark:border-gray-700 overflow-hidden">

        {/* Tab bar */}
        <div className="flex items-center gap-1 px-5 pt-4 pb-0 border-b border-gray-100 dark:border-gray-700 bg-gray-50/30 dark:bg-gray-800/50">
          {TABS.map(({ key, label, icon }) => {
            const isActive = activeTab === key;
            const count = tabCount(
              key === null       ? null
              : key === 'flows'      ? 'Flow'
              : key === 'dashboards' ? 'Dashboard'
              : 'Data Model'
            );
            return (
              <button
                key={String(key)}
                onClick={() => handleTabChange(key)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium rounded-t-lg
                           border-b-2 transition-all duration-200 -mb-px
                  ${isActive
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/30'
                  }`}
              >
                <DynamicIcon name={icon} className="w-4 h-4" />
                {label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-0.5
                  ${isActive
                    ? count > 0
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                      : 'bg-blue-50 text-blue-400 dark:bg-blue-900/30 dark:text-blue-500'
                    : count > 0
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                      : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}

          {/* Refresh button */}
          <div className="ml-auto pb-2">
            <button
              onClick={() => load(activeTab, page)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 transition-colors"
              title="Refresh"
            >
              <DynamicIcon name="RefreshCw" className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3">
            <LoadingSpinner />
            <span className="text-sm text-gray-400">Loading…</span>
          </div>
        ) : items.length === 0 ? (
          <EmptyState filtered={activeTab !== null} />
        ) : (
          <>
            <div>
              {items.map((item, idx) => (
                <ItemRow
                  key={`${item.table}-${item.id}-${idx}`}
                  item={item}
                  onRestore={setRestoreTarget}
                  onPurge={setPurgeTarget}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3
                              border-t border-gray-100 dark:border-gray-700
                              bg-gray-50/50 dark:bg-gray-800/50">
                <p className="text-xs text-gray-400">
                  Page {page} of {totalPages} · {total} item{total !== 1 ? 's' : ''}
                </p>
                <div className="flex gap-1">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg
                               border border-gray-200 dark:border-gray-600
                               hover:bg-gray-100 dark:hover:bg-gray-700
                               disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg
                               border border-gray-200 dark:border-gray-600
                               hover:bg-gray-100 dark:hover:bg-gray-700
                               disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Restore Confirmation ────────────────────────────────────────────── */}
      <ConfirmModal
        isOpen={!!restoreTarget}
        title="Restore Item"
        onClose={() => setRestoreTarget(null)}
        onConfirm={handleRestore}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-900/20
                          flex items-center justify-center shrink-0">
            <DynamicIcon name="RotateCcw" className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Restore <strong>"{restoreTarget?.name}"</strong>?
            </p>
            <p className="text-xs text-gray-400 mt-1">
              This {restoreTarget?.type} will become active again and visible in its original location.
            </p>
          </div>
        </div>
        {acting && (
          <div className="flex items-center gap-2 text-green-600 mt-2">
            <LoadingSpinner size="sm" />
            <span className="text-sm">Restoring…</span>
          </div>
        )}
      </ConfirmModal>

      {/* ─── Purge Confirmation ──────────────────────────────────────────────── */}
      <ConfirmModal
        isOpen={!!purgeTarget}
        title="Delete Forever"
        onClose={() => setPurgeTarget(null)}
        onConfirm={handlePurge}
      >
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                        rounded-xl p-3 mb-4">
          <p className="text-sm text-red-700 dark:text-red-300 font-semibold flex items-center gap-2">
            <DynamicIcon name="AlertTriangle" className="w-4 h-4 shrink-0" />
            This action cannot be undone
          </p>
        </div>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Permanently delete <strong>"{purgeTarget?.name}"</strong>?
          <span className="block text-xs text-gray-400 mt-1">
            This {purgeTarget?.type} will be removed from the database forever.
          </span>
        </p>
        {acting && (
          <div className="flex items-center gap-2 text-red-600 mt-3">
            <LoadingSpinner size="sm" />
            <span className="text-sm">Deleting…</span>
          </div>
        )}
      </ConfirmModal>
    </div>
  );
};

export default MyTrashPage;
